/**
 * Account Management Module
 * 
 * Handles logged-in user account features:
 * - Wallet (Deposit/Withdraw)
 * - Account limits (Loss, Session, Wager, Deposit)
 * - Self-exclusion and cooling off
 * - Profile management (Change password, 2FA, Verification)
 * - Bonus management
 * 
 * Note: Authentication (Login/Signup) is handled by buttons-account-login.js modal flow
 * 
 * @package betbeast
 */

import { showCustomHint } from './custom-hint.js';
import { initializePaymentIQForm } from './payment-iq-cashier.js';
import { depositFlow } from './deposit-flow/deposit-flow-controller.js';
import { withdrawalFlow } from './withdrawal-flow/withdrawal-flow-controller.js';
import { phoneCountryVerdict, phoneCountryMessage } from '../modules/phone-country-rules.js';
import { offerSupport } from '../modules/support-offer.js';

// =========================================================================
// FORM DATA HIDDEN HELPERS (for session-less data persistence)
// =========================================================================

function updateAndReturnFormData(formData) {
    if (!formData || typeof formData !== 'object') {
        return null;
    }

    const {
        step,
        action,
        nonce,
        form_data_hidden,
        password,
        ...dataToSave
    } = formData;

    const existingData = loadFormDataFromHidden();
    const updatedData = { ...existingData, ...dataToSave };

    saveFormDataToHidden(updatedData);

    return {
        ...updatedData,
        form_data_hidden: btoa(JSON.stringify(updatedData)),
        step,
        action,
        nonce,
        password,
    };
}

function saveFormDataToHidden(formData) {
    const hidden = document.getElementById('form_data_hidden');
    if (!hidden) return;

    const sStringify = (JSON.stringify(formData));
    hidden.value = sStringify;
}

function loadFormDataFromHidden() {
    const hidden = document.getElementById('form_data_hidden');
    if (!hidden || !hidden.value) return {};

    try {
        return JSON.parse(hidden.value);
    } catch (e) {
        return {};
    }
}

function clearFormData() {
    const hidden = document.getElementById('form_data_hidden');
    if (!hidden || !hidden.value) return;

    const formData = {};
    hidden.value = '';
}

// =========================================================================
// MAIN MODULE
// =========================================================================

(function() {
    'use strict';

    // =========================================================================
    // INITIALIZATION
    // =========================================================================
    
    document.addEventListener('DOMContentLoaded', function() {
        initPasswordToggles();
        initLogout();
        initChangePassword();
        initTwoFactorAuth();
        initVerification();
        initExpertMode();
        initAccountLimits();
        initAddMobilePhone();
        showWageringHint();

        const provider = ((getAccountData().cashierProvider || '') + '').toLowerCase();
        if (provider === 'paymentiq') {
            initWallet();
            initBonusCashier();
            return;
        }

        initDepositFlow();
        initWithdrawalFlow();
    });

    function showWageringHint () {
        document.querySelectorAll('.show-wagering-hint').forEach(function (element) {

            element.addEventListener('click', function (e) {

                const formData = {
                    action: 'can_withdraw',
                    nonce: accountBonusesAjax.nonce
                };

                jQuery.post(ajaxobject.ajaxurl, formData, (response) => {

                    if (response && response.data) {

                        if (response.success && response.data.redirect) {
                            window.location = response.data.redirect;
                        } else if (!response.success && response.data.message) {
                            showCustomHint(response.data.message, 'error');
                        }

                    }

                });

                e.preventDefault();

                return false;


            });
        });
    };


    // =========================================================================
    // UTILITY FUNCTIONS
    // =========================================================================

    /**
     * Get account data (nonce, ajax URL)
     * @returns {Object}
     */
    function getAccountData() {
        return typeof accountData !== 'undefined' ? accountData : {};
    }

    // Hard ceiling for an account-page submit. jQuery.post has no timeout option, so
    // a stalled request never settles, `.always()` below never fires, and the button
    // stays disabled forever — the same stuck UI already fixed for the auth modals.
    // Worse here: several of these forms sit behind login (change password, 2FA), and
    // the only way out of a dead button is a page reload. Mirrors SUBMIT_TIMEOUT_MS in
    // auth-flow-services/ajax-service.js — keep the two values in sync.
    const SUBMIT_TIMEOUT_MS = 15000;

    /**
     * Make AJAX POST request
     * @param {Object} formData - Data to send
     * @param {Function} onSuccess - Success callback
     * @param {Function} onError - Error callback (optional)
     * @param {HTMLElement} button - Button to disable during request (optional)
     */
    function ajaxPost(formData, onSuccess, onError, button) {
        const data = getAccountData();
        const url = data.ajaxUrl || (typeof ajaxobject !== 'undefined' ? ajaxobject.ajaxurl : '');

        if (button) {
            button.disabled = true;
            button.classList.add('ajax');
        }

        jQuery.ajax({
            url: url,
            type: 'POST',
            data: formData,
            dataType: 'json',
            timeout: SUBMIT_TIMEOUT_MS,
        })
        .done(function(response) {
            if (response.success) {
                onSuccess(response);
            } else {
                if (onError) {
                    onError(response);
                } else {
                    showCustomHint(response.data.message, 'error');
                }
            }
        })
        .fail(function(jqXHR, textStatus) {
            // There was no fail branch at all before, so an outright network failure
            // showed the user nothing. Hand callers the same `{data:{message}}` shape
            // the non-success branch gives them, so their onError keeps working.
            const message = 'timeout' === textStatus
                ? (data.requestTimeoutMessage || 'The request timed out. Please check your connection and try again.')
                : 'An error occurred. Please try again.';

            if (onError) {
                onError({ data: { message } });
            } else {
                showCustomHint(message, 'error');
            }
        })
        .always(function() {
            if (button) {
                setTimeout(function() {
                    button.disabled = false;
                    button.classList.remove('ajax');
                }, 1000);
            }
        });
    }

    /**
     * Show field error
     * @param {HTMLElement} form
     * @param {string} inputName
     * @param {string} type
     */
    function showFieldError(form, inputName, type) {
        const errorLabel = form.querySelector('.error-' + inputName);
        const errorMessage = form.querySelector('.error-' + inputName + ' .' + type);
        if (errorLabel && errorMessage) {
            errorLabel.style.display = 'block';
            errorMessage.style.display = 'block';
        }
    }

    /**
     * Hide field error
     * @param {HTMLElement} form
     * @param {string} inputName
     * @param {string} type
     */
    function hideFieldError(form, inputName, type) {
        const errorLabel = form.querySelector('.error-' + inputName);
        const errorMessage = form.querySelector('.error-' + inputName + ' .' + type);
        if (errorLabel && errorMessage) {
            errorLabel.style.display = 'none';
            errorMessage.style.display = 'none';
        }
    }

    // =========================================================================
    // PASSWORD TOGGLE (Reusable)
    // =========================================================================

    /**
     * Initialize all password toggle functionality
     */
    function initPasswordToggles() {
        // Initialize password toggles for various password fields in account details
        const passwordConfigs = [
            { input: 'input[name="password"]', container: '.your-account', eyeClass: 'password-eye-icon', slashClass: 'password-eye-slash-icon' },
            { input: 'input[name="new_password"]', container: '.your-account', eyeClass: 'new-password-eye-icon', slashClass: 'new-password-eye-slash-icon' },
            { input: 'input[name="confirm_password"]', container: '.your-account', eyeClass: 'confirm-password-eye-icon', slashClass: 'confirm-password-eye-slash-icon' }
        ];

        passwordConfigs.forEach(function(config) {
            initSinglePasswordToggle(config);
        });

        // Initialize password toggle for auth modal (change password)
        initAuthModalPasswordToggle();
    }

    /**
     * Initialize a single password toggle
     * @param {Object} config - Configuration object with input, container, eyeClass, slashClass
     */
    function initSinglePasswordToggle(config) {
        const passwordInput = document.querySelector(config.input);
        const eyeIcon = document.querySelector(config.container + ' .' + config.eyeClass);
        const eyeSlashIcon = document.querySelector(config.container + ' .' + config.slashClass);

        if (!passwordInput || !eyeIcon || !eyeSlashIcon) {
            return;
        }

        function toggleVisibility() {
            const currentValue = passwordInput.value;
            const isPassword = passwordInput.type === 'password';
            
            // Fix for iOS: clear and refocus before changing type
            passwordInput.value = '';
            passwordInput.focus();
            
            if (isPassword) {
                passwordInput.type = 'text';
                eyeIcon.classList.add('hidden');
                eyeSlashIcon.classList.remove('hidden');
            } else {
                passwordInput.type = 'password';
                eyeIcon.classList.remove('hidden');
                eyeSlashIcon.classList.add('hidden');
            }
            
            // Restore value after type change
            passwordInput.value = currentValue;
        }

        eyeIcon.addEventListener('click', toggleVisibility);
        eyeSlashIcon.addEventListener('click', toggleVisibility);
    }

    /**
     * Initialize password toggle for auth modal (change password)
     */
    function initAuthModalPasswordToggle() {
        const modal = document.querySelector('#auth-modal-change-password');
        if (!modal) return;

        const toggleButton = modal.querySelector('[data-password-toggle]');
        if (!toggleButton) return;

        const passwordInput = modal.querySelector('input[name="password"]');
        const eyeIcon = toggleButton.querySelector('.password-eye-icon');
        const eyeSlashIcon = toggleButton.querySelector('.password-eye-slash-icon');

        if (!passwordInput || !eyeIcon || !eyeSlashIcon) return;

        toggleButton.addEventListener('click', function() {
            const currentValue = passwordInput.value;
            const isPassword = passwordInput.type === 'password';
            
            // Fix for iOS: clear and refocus before changing type
            passwordInput.value = '';
            passwordInput.focus();
            
            if (isPassword) {
                passwordInput.type = 'text';
                eyeIcon.classList.add('hidden');
                eyeSlashIcon.classList.remove('hidden');
                toggleButton.setAttribute('aria-pressed', 'true');
                toggleButton.setAttribute('aria-label', 'Hide password');
            } else {
                passwordInput.type = 'password';
                eyeIcon.classList.remove('hidden');
                eyeSlashIcon.classList.add('hidden');
                toggleButton.setAttribute('aria-pressed', 'false');
                toggleButton.setAttribute('aria-label', 'Show password');
            }
            
            // Restore value after type change
            passwordInput.value = currentValue;
        });
    }

    // =========================================================================
    // BONUS CASHIER
    // =========================================================================

    function initBonusCashier() {

        const bonusesButton = document.querySelector('.bonuses-list input');
        if (!bonusesButton) {
            return;
        }

        document.querySelectorAll('.bonuses-list input').forEach(input => {
            input.addEventListener('change', function(event) {

                event.preventDefault();
                const currentInput = this;

                const paymentContainer = document.querySelector('.your-account.wallet-deposit-payment-container');
                if (!paymentContainer) {
                    return;
                }

                document.querySelectorAll('.bonuses-list input').forEach(bonusInput => {
                    if (currentInput != bonusInput) {
                        bonusInput.checked = false;
                    }
                });

                let bonusCode = '';

                if (this.checked) {

                    bonusCode = this.value;

                }

                const partyId = paymentContainer.dataset.partyId;
                const sessionKey = paymentContainer.dataset.sessionKey;

                initializePaymentIQForm(
                    partyId,
                    sessionKey,
                    '',
                    bonusCode,
                    'DEPOSIT'
                );

            });
        });
    }

    // =========================================================================
    // LOGOUT
    // =========================================================================

    function initLogout() {
        const logoutButton = document.querySelector('.your-account .logout');
        if (!logoutButton) return;

        logoutButton.addEventListener('click', function(event) {
            event.preventDefault();

            const formData = {
                action: 'bb_account_logout',
                nonce: getAccountData().nonce,
            };

            ajaxPost(formData, function(response) {
                if (window.bbOneSignal && typeof window.bbOneSignal.logout === 'function') {
                    window.bbOneSignal.logout();
                }

                window.dispatchEvent(new CustomEvent('bb:account-logout-success'));
                showCustomHint(response.data.message, 'success');
                window.location.href = response.data.redirect_url;
            });
        });
    }

    // =========================================================================
    // ADD MOBILE PHONE
    // =========================================================================

    /**
     * Country phone rule on the account page: a point call to FormValidation for the
     * highlight only (decision 41). The Change button keeps its own gate and never
     * asks FormValidation, so nothing here can stop a save. Off: nothing is attached.
     */
    function initMobilePhoneCountryCheck() {
        const data         = getAccountData();
        const countryRules = data.validationRules && data.validationRules.phone ? data.validationRules.phone.country_rules : null;
        const form         = document.querySelector('#auth-mobile-phone');
        const input        = form ? form.querySelector('input[name="phone"]') : null;
        if (!countryRules || !form || !input || !window.FormValidation) return;

        const saved = input.value;
        const check = function () {
            if (!input.value.trim()) return;
            const result = window.FormValidation.validate(input, form);

            // The number already on the account gets its own wording (plan 7.6).
            if (result && result.notice && saved && input.value === saved) {
                const verdict = phoneCountryVerdict(input.value, 'account', countryRules, form.dataset.accountCountry || '');
                const field   = input.closest('.field');
                const message = field ? field.nextElementSibling : null;
                if (message && message.classList.contains('field__validation-message')) {
                    message.textContent = phoneCountryMessage(verdict, countryRules, window.COUNTRY_PHONE_DATA, true);
                }
            }
        };

        input.addEventListener('blur', check);
        input.addEventListener('input', function () {
            if (input.classList.contains('is-touched')) check();
        });
        input.addEventListener('blur', function () { input.classList.add('is-touched'); });

        if (saved) check();
    }

    function initAddMobilePhone() {
        const addPhoneButton = document.querySelector('.your-account .add-mobile-phone-button');
        if (!addPhoneButton) return;

        initMobilePhoneCountryCheck();

        addPhoneButton.addEventListener('click', function(event) {
            event.preventDefault();
            
            const form = document.querySelector('#auth-mobile-phone');
            if (!form) {
                showCustomHint('Form not found', 'error');
                return;
            }

            const phoneInput = form.querySelector('input[name="phone"]');
            if (!phoneInput || phoneInput.value === '') {
                showCustomHint('Phone cannot be empty', 'error');
                showFieldError(form, 'phone', 'required');
                return;
            }

            const formData = {
                action: 'bb_account_add_mobile_phone',
                nonce: getAccountData().nonce,
                phone: phoneInput.value,
            };

            ajaxPost(formData, function(response) {
                showCustomHint(response.data.message, 'success');
                window.location.reload();
            }, function(response) {
                const data = response && response.data ? response.data : {};
                showCustomHint(data.message, 'error');
                // The platform's attempts are used up: only an agent can change the record.
                if (data.support) {
                    offerSupport(addPhoneButton, data.support, data.message);
                }
            }, addPhoneButton);
        });
    }

    // =========================================================================
    // CHANGE PASSWORD (Account Details)
    // =========================================================================

    function initChangePassword() {
        const changeButton = document.querySelector('.your-account .details-change-password-button');
        if (!changeButton) return;

        changeButton.addEventListener('click', function(event) {
            event.preventDefault();

            const form = document.querySelector('#account-details-change-password');
            if (!form) {
                showCustomHint('Form not found', 'error');
                return;
            }

            const currentPasswordInput = form.querySelector('input[name="password"]');
            const newPasswordInput = form.querySelector('input[name="new_password"]');
            const confirmPasswordInput = form.querySelector('input[name="confirm_password"]');

            if (!currentPasswordInput || !newPasswordInput || !confirmPasswordInput) {
                showCustomHint('Form inputs not found', 'error');
                return;
            }

            // Validation
            if (currentPasswordInput.value === '') {
                showCustomHint('Current password cannot be empty', 'error');
                showFieldError(form, 'password', 'required');
                return;
            }
            if (newPasswordInput.value === '') {
                showCustomHint('New password cannot be empty', 'error');
                showFieldError(form, 'new_password', 'required');
                return;
            }
            if (confirmPasswordInput.value === '') {
                showCustomHint('Confirm password cannot be empty', 'error');
                showFieldError(form, 'confirm_password', 'required');
                return;
            }
            if (newPasswordInput.value !== confirmPasswordInput.value) {
                showCustomHint('New password and confirm password do not match', 'error');
                showFieldError(form, 'new_password', 'invalid');
                showFieldError(form, 'confirm_password', 'invalid');
                return;
            }

            const formData = {
                action: 'bb_account_details_change_password',
                nonce: getAccountData().nonce,
                current_password: currentPasswordInput.value,
                new_password: newPasswordInput.value,
                confirm_password: confirmPasswordInput.value,
            };

            ajaxPost(formData, function(response) {
                showCustomHint(response.data.message, 'success');
                window.location.reload();
            }, null, changeButton);
        });
    }

    // =========================================================================
    // TWO-FACTOR AUTHENTICATION
    // =========================================================================

    function initTwoFactorAuth() {
        const twoFactorButton = document.querySelector('.your-account .two-factor-auth-button');
        if (!twoFactorButton) return;

        twoFactorButton.addEventListener('click', function(event) {
            event.preventDefault();

            const form = document.querySelector('#account-two-factor-auth');
            if (!form) {
                showCustomHint('Form not found', 'error');
                return;
            }

            const authCodeInput = form.querySelector('input[name="authentication_code"]');
            if (!authCodeInput || authCodeInput.value === '') {
                showCustomHint('Authentication code cannot be empty', 'error');
                showFieldError(form, 'authentication_code', 'required');
                return;
            }

            const formData = {
                action: 'bb_account_two_factor_auth',
                nonce: getAccountData().nonce,
                auth_code: authCodeInput.value,
            };

            ajaxPost(formData, function(response) {
                showCustomHint(response.data.message, 'success');
                window.location.reload();
            }, null, twoFactorButton);
        });
    }

    // =========================================================================
    // VERIFICATION DOCUMENTS
    // =========================================================================

    function initVerification() {
        const verifyButton = document.querySelector('.your-account .verification-documents-button');
        if (!verifyButton) return;

        verifyButton.addEventListener('click', function(event) {
            event.preventDefault();

            const form = document.querySelector('#account-verification-documents');
            if (!form) {
                showCustomHint('Form not found', 'error');
                return;
            }

            const typeInput = form.querySelector('input[name="type"]');
            const descInput = form.querySelector('input[name="description"]');
            const fileInput = form.querySelector('input[name="file"]');

            if (!typeInput || typeInput.value === '') {
                showCustomHint('Document type cannot be empty', 'error');
                return;
            }
            if (!descInput || descInput.value === '') {
                showCustomHint('Document description cannot be empty', 'error');
                showFieldError(form, 'description', 'required');
                return;
            }
            if (!fileInput || fileInput.files.length === 0) {
                showCustomHint('Please select a file to upload', 'error');
                showFieldError(form, 'file', 'required');
                return;
            }

            const formData = new FormData();
            formData.append('action', 'bb_account_verification_documents');
            formData.append('nonce', getAccountData().nonce);
            formData.append('description', descInput.value);
            formData.append('type', typeInput.value);
            formData.append('file', fileInput.files[0]);

            verifyButton.disabled = true;
            verifyButton.classList.add('ajax');

            jQuery.ajax({
                url: getAccountData().ajaxUrl,
                type: 'POST',
                data: formData,
                cache: false,
                contentType: false,
                processData: false,
                success: function(response) {
                    if (response.success) {
                        showCustomHint(response.data.message, 'success');
                        window.location.reload();
                    } else {
                        showCustomHint(response.data.message, 'error');
                    }
                },
                error: function() {
                    showCustomHint('An unexpected error occurred.', 'error');
                },
                complete: function() {
                    setTimeout(function() {
                        verifyButton.disabled = false;
                        verifyButton.classList.remove('ajax');
                    }, 1000);
                }
            });
        });
    }

    // =========================================================================
    // EXPERT MODE
    // =========================================================================

    function initExpertMode() {
        const expertModeCheckbox = document.querySelector('.your-account input[name="expert_mode"]');
        if (!expertModeCheckbox) return;

        expertModeCheckbox.addEventListener('click', function(event) {
            event.preventDefault();

            const formData = {
                action: 'bb_account_limits_expert_mode',
                nonce: getAccountData().nonce,
                expert_mode: expertModeCheckbox.checked ? 1 : 0,
            };

            ajaxPost(formData, function(response) {
                showCustomHint(response.data.message, 'success');
                window.location.reload();
            });
        });
    }

    // =========================================================================
    // ACCOUNT LIMITS
    // =========================================================================

    function initAccountLimits() {
        initLimitForm('loss-limit', 'bb_account_limits_loss_limit', ['period', 'limit_eur', 'limit_rub', 'limit_pln', 'limit_btc']);
        initLimitForm('session-limit', 'bb_account_limits_session_limit', ['period']);
        initLimitForm('wager-limit', 'bb_account_limits_wager_limit', ['period', 'limit_eur', 'limit_rub', 'limit_pln', 'limit_btc']);
        initLimitForm('deposit-limit', 'bb_account_limits_deposit_limit', ['period', 'limit_eur', 'limit_rub', 'limit_pln', 'limit_btc']);
        initLimitForm('self-exclusion', 'bb_account_limits_self_exclusion', ['period']);
        initLimitForm('cooling-off', 'bb_account_limits_cooling_off', ['period']);
    }

    /**
     * Initialize a limit form
     * @param {string} formType - Form type identifier (e.g., 'loss-limit')
     * @param {string} action - AJAX action name
     * @param {Array} fields - Array of field names to collect
     */
    function initLimitForm(formType, action, fields) {
        const button = document.querySelector('.your-account .' + formType + '-button');
        if (!button) return;

        button.addEventListener('click', function(event) {
            event.preventDefault();

            const form = document.querySelector('#account-' + formType);
            if (!form) {
                showCustomHint('Form not found', 'error');
                return;
            }

            const formData = {
                action: action,
                nonce: getAccountData().nonce,
            };

            // Collect field values
            let missingFields = false;
            fields.forEach(function(fieldName) {
                const input = form.querySelector('[name="' + fieldName + '"]');
                if (!input) {
                    missingFields = true;
                    return;
                }
                formData[fieldName] = input.value;
            });

            if (missingFields) {
                showCustomHint('Form inputs not found', 'error');
                return;
            }

            ajaxPost(formData, function(response) {
                showCustomHint(response.data.message, 'success');
                window.location.reload();
            }, null, button);
        });
    }

    // =========================================================================
    // WALLET (Deposit/Withdraw)
    // =========================================================================

    function initWallet() {
        const depositContainer = document.querySelector('.your-account.wallet-deposit-payment-container');
        if (depositContainer) {
            initializePaymentIQForm(
                depositContainer.dataset.partyId,
                depositContainer.dataset.sessionKey,
                '',
                depositContainer.dataset.bonusCode || '',
                'DEPOSIT'
            );
        }

        const withdrawContainer = document.querySelector('.your-account.wallet-withdraw-payment-container');
        if (withdrawContainer) {
            initializePaymentIQForm(
                withdrawContainer.dataset.partyId,
                withdrawContainer.dataset.sessionKey,
                '',
                withdrawContainer.dataset.bonusCode || '',
                'WITHDRAWAL'
            );
        }
    }

    // =========================================================================
    // DEPOSIT FLOW (Modal-based)
    // =========================================================================

    function initDepositFlow() {
        depositFlow.init();
    }

    // =========================================================================
    // WITHDRAWAL FLOW (Modal-based)
    // =========================================================================

    function initWithdrawalFlow() {
        withdrawalFlow.init();
    }

    // Expose utility functions for external use
    window.showFieldError = showFieldError;
    window.hideFieldError = hideFieldError;
    window.depositFlow = depositFlow;
    window.withdrawalFlow = withdrawalFlow;

})();